import type { WeightSampleLog } from '../../types/production'
import type { BiomassDataRow } from '../../utils/productionUnitTable'

import {
  buildDailyWeightRollupKey,
  rollupWeightSamplesByLotDay,
  type DailyWeightRollup
} from '../../utils/dailyWeightRollup'
import { toLocalCalendarDayKey } from '../../utils/dateNormalization'
import { farmTimeZone } from '../../utils/farmTime'

type BiomassWeightSampleMetadata = {
  productionRowId?: string
  lotId?: string
  stage?: 'Active' | 'Inactive'
}

const toTrimmedString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

const parseBiomassWeightSampleMetadata = (log: WeightSampleLog): BiomassWeightSampleMetadata => {
  const metadata = log.metadata
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return {}

  const stage = toTrimmedString(metadata.stage)
  let normalizedStage: 'Active' | 'Inactive' | undefined
  if (stage === 'Inactive') normalizedStage = 'Inactive'
  else if (stage === 'Active') normalizedStage = 'Active'

  return {
    productionRowId: toTrimmedString(metadata.productionRowId),
    lotId: toTrimmedString(metadata.lotId),
    stage: normalizedStage
  }
}

const toCalendarDateKey = (value: string | undefined | null, timeZone?: string): string => {
  // Local-day keying matches `overlayMortalityLogsOnProductionRows` — see
  // [docs/release/v2.1/findings.md](../../../docs/release/v2.1/findings.md)
  // row #7. Without this, evening rows in behind-UTC time zones split off
  // into their own bucket and the WSL trust signal disagrees with what
  // users see on the page.
  return toLocalCalendarDayKey(value, timeZone)
}

const buildRollupKeyForRow = (rowDate: string, lot: string | undefined, timeZone?: string): string => {
  return buildDailyWeightRollupKey(toCalendarDateKey(rowDate, timeZone), String(lot ?? '').trim())
}

/**
 * Build the metadata payload written to a `WeightSampleLog` at save time so
 * the read side can promote the log captured *for* this row above the other
 * contributing logs. Mirrors `buildBiomassSamplingMortalityMetadata`.
 */
export const buildBiomassSamplingWeightMetadata = (args: {
  productionRowId: string
  lotId: string
  stage: 'Active' | 'Inactive'
}): Record<string, unknown> => ({
  source: 'biomassSampling',
  productionRowId: args.productionRowId,
  lotId: args.lotId,
  stage: args.stage
})

/**
 * Find the single `WeightSampleLog` linked to this row for the
 * "edit-this-event" affordance. Prefers a metadata-tagged match (the log
 * captured *for* this row), then falls back to the first log matching
 * `(calendar date, lot)`.
 *
 * This is intentionally 1:1. For the canonical *daily mean* (which averages
 * every event on the same day for the same lot) use
 * `findAllLinkedWeightSampleLogsForRow` or read `row.weight` after
 * `overlayWeightSamplesOnProductionRows`.
 */
export const findLinkedWeightSampleLog = (
  row: Pick<BiomassDataRow, 'id' | 'date' | 'lot'>,
  logs: WeightSampleLog[]
): WeightSampleLog | undefined => {
  const rowDateKey = toCalendarDateKey(row.date)
  if (!rowDateKey) {
    return logs.find((log) => parseBiomassWeightSampleMetadata(log).productionRowId === row.id)
  }

  const rollupKey = buildRollupKeyForRow(row.date, row.lot)
  let fallbackMatch: WeightSampleLog | undefined

  for (const log of logs) {
    const metadata = parseBiomassWeightSampleMetadata(log)
    if (metadata.productionRowId === row.id) return log
    if (!fallbackMatch && buildRollupKeyForRow(log.recordedAt, log.lot, farmTimeZone(log.farmId)) === rollupKey) {
      fallbackMatch = log
    }
  }

  return fallbackMatch
}

/**
 * Return every `WeightSampleLog` whose `(calendar date, lot)` matches this
 * row, in input order, with the metadata-tagged log promoted to the front
 * when one exists. Used by trust-signal UI (the "derived from N events"
 * tooltip) and any consumer that needs per-event detail beyond the
 * aggregated mean.
 */
/**
 * v2.2 findings.md #65: the sampling drill-down popover used to list only
 * logs whose `productionRowId` equals the row id. Crew Mode captures never
 * set `productionRowId` (weightSampleCaptureValidation.ts), so every crew
 * sample was missing from the popover — and with it the only web path to
 * correct one — even though the row's weight already included it (the
 * overlay matches by `(calendar date, lot)`). Union of both: the row-id
 * matches plus every same-unit log matched the way the overlay matches,
 * de-duplicated by id.
 */
export const collectSamplingEventsForRow = (
  row: Pick<BiomassDataRow, 'id' | 'date' | 'lot'>,
  logsLinkedByRowId: WeightSampleLog[],
  unitLogs: WeightSampleLog[]
): WeightSampleLog[] => {
  const byId = new Map<string, WeightSampleLog>()
  for (const log of logsLinkedByRowId) byId.set(log.id, log)
  for (const log of findAllLinkedWeightSampleLogsForRow(row, unitLogs)) {
    if (!byId.has(log.id)) byId.set(log.id, log)
  }
  return Array.from(byId.values())
}

export const findAllLinkedWeightSampleLogsForRow = (
  row: Pick<BiomassDataRow, 'id' | 'date' | 'lot'>,
  logs: WeightSampleLog[]
): WeightSampleLog[] => {
  const rowDateKey = toCalendarDateKey(row.date)
  if (!rowDateKey) return []

  const rollupKey = buildRollupKeyForRow(row.date, row.lot)
  const matched: WeightSampleLog[] = []
  let metadataMatchIndex = -1

  for (const log of logs) {
    if (buildRollupKeyForRow(log.recordedAt, log.lot, farmTimeZone(log.farmId)) !== rollupKey) continue
    const metadata = parseBiomassWeightSampleMetadata(log)
    if (metadataMatchIndex === -1 && metadata.productionRowId === row.id) {
      metadataMatchIndex = matched.length
    }
    matched.push(log)
  }

  if (metadataMatchIndex > 0) {
    const [primary] = matched.splice(metadataMatchIndex, 1)
    matched.unshift(primary)
  }

  return matched
}

const pickPrimaryLogForRow = (
  rowId: string,
  rollup: DailyWeightRollup,
  logsById: Map<string, WeightSampleLog>
): WeightSampleLog | undefined => {
  // Prefer a metadata-tagged log for this row, then the most recently
  // recorded contributing log so the "edit this event" affordance points at
  // something operationally meaningful.
  for (const logId of rollup.logIds) {
    const log = logsById.get(logId)
    if (!log) continue
    if (parseBiomassWeightSampleMetadata(log).productionRowId === rowId) return log
  }
  for (let i = rollup.logIds.length - 1; i >= 0; i -= 1) {
    const log = logsById.get(rollup.logIds[i])
    if (log) return log
  }
  return undefined
}

/**
 * Overlay the canonical daily mean weight onto each production row.
 *
 * For every row that has at least one matching `WeightSampleLog` (matched by
 * `(calendar date, lot)`), the row's `weight` is replaced with the
 * arithmetic mean of every matching log's `weightGrams` (v2.1 finding #60
 * resolution: events are canonical when they exist; the stored
 * `ProductionRecord.weightGrams` becomes display-derived only, matching how
 * `overlayMortalityLogsOnProductionRows` treats `dailyMortality`).
 *
 * The row also carries:
 * - `weightSampleLogId` — id of the metadata-tagged log for that row, or the
 *   most recent contributing log when no metadata-tagged log exists. Anchors
 *   the "edit this event" affordance.
 * - `weightSampleLogCount` — string-encoded number of contributing logs. UI
 *   consumers parse this to render the "derived from N events" trust badge.
 *
 * Rows without matching logs are returned untouched (referential equality
 * preserved).
 */
export const overlayWeightSamplesOnProductionRows = (
  rows: BiomassDataRow[],
  logs: WeightSampleLog[]
): BiomassDataRow[] => {
  if (rows.length === 0 || logs.length === 0) return rows

  const rollupByKey = rollupWeightSamplesByLotDay(logs)
  if (rollupByKey.size === 0) return rows

  const logsById = new Map<string, WeightSampleLog>()
  for (const log of logs) {
    if (typeof log.id === 'string' && log.id) {
      logsById.set(log.id, log)
    }
  }

  let changed = false
  const nextRows = rows.map((row) => {
    const key = buildRollupKeyForRow(row.date, row.lot)
    const rollup = rollupByKey.get(key)
    if (!rollup) return row

    const primaryLog = pickPrimaryLogForRow(row.id, rollup, logsById)
    if (!primaryLog) return row

    const metadata = parseBiomassWeightSampleMetadata(primaryLog)
    const nextWeight = rollup.meanWeightGrams
    const nextLotId = row.lotId ?? metadata.lotId
    const nextStage = metadata.stage ?? row.stage
    const nextWeightSampleLogId = primaryLog.id
    const nextWeightSampleLogCount = String(rollup.eventCount)

    if (
      row.weight === nextWeight
      && row.lotId === nextLotId
      && row.stage === nextStage
      && row.weightSampleLogId === nextWeightSampleLogId
      && row.weightSampleLogCount === nextWeightSampleLogCount
    ) {
      return row
    }

    changed = true
    return {
      ...row,
      weight: nextWeight,
      lotId: nextLotId,
      stage: nextStage,
      weightSampleLogId: nextWeightSampleLogId,
      weightSampleLogCount: nextWeightSampleLogCount
    }
  })

  return changed ? nextRows : rows
}

/**
 * Sidecar map from production-row id to the canonical event-derived weight
 * rollup for that `(calendar date, lot)`. Returned without mutating the
 * input rows so screens that need to keep `data` as their editable source of
 * truth (notably the Database screen) can render trust signals next to the
 * stored value instead of overwriting it.
 *
 * Rows whose `(calendar date, lot)` does not match any log are omitted.
 */
export const computeWeightSampleRollupByRowId = (
  rows: Pick<BiomassDataRow, 'id' | 'date' | 'lot'>[],
  logs: WeightSampleLog[]
): Map<string, { meanWeightGrams: string; eventCount: number; primaryLogId: string | undefined }> => {
  const out = new Map<string, { meanWeightGrams: string; eventCount: number; primaryLogId: string | undefined }>()
  if (rows.length === 0 || logs.length === 0) return out

  const rollupByKey = rollupWeightSamplesByLotDay(logs)
  if (rollupByKey.size === 0) return out

  const logsById = new Map<string, WeightSampleLog>()
  for (const log of logs) {
    if (typeof log.id === 'string' && log.id) logsById.set(log.id, log)
  }

  for (const row of rows) {
    const key = buildRollupKeyForRow(row.date, row.lot)
    const rollup = rollupByKey.get(key)
    if (!rollup || rollup.eventCount === 0) continue
    const primary = pickPrimaryLogForRow(row.id, rollup, logsById)
    out.set(row.id, {
      meanWeightGrams: rollup.meanWeightGrams,
      eventCount: rollup.eventCount,
      primaryLogId: primary?.id
    })
  }

  return out
}
